<!-- Metric_Alert_Instance_Overview.vue -->
<template>
  <div v-if="alertInstance" class="bg-gray-50">
    <!-- Header Section with Background Pattern -->
    <div class="relative bg-gray-50 py-14 border-t"
      :style="{
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPositionX: '-350px',
        backgroundPositionY: '37%',
        backgroundSize: '800px auto'
      }">
      <div class="px-8">
        <div class="px-6">
          <div>
            <h3 class="text-2xl font-bold text-gray-900">
              {{ alertInstance.alert_name }}
            </h3>
            <p class="text-sm text-gray-500 mt-1">
              Alert triggered on {{ formatDate(alertInstance.observation_date) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="px-8 py-6 space-y-6 max-w-7xl mx-auto">

      <!-- Alert Overview Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div class="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon :icon="['fas', 'bell']" class="mr-2 text-orange-600" />
              Alert Details
            </h2>
            <span :class="getStatusClass(alertInstance.status)">
              {{ formatStatus(alertInstance.status) }}
            </span>
          </div>
        </div>

        <div class="p-6">
          <dl class="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
            <div class="space-y-1">
              <dt class="text-sm font-medium text-gray-500">Entity</dt>
              <dd class="text-sm text-gray-900">
                <button @click="navigateToEntity" class="text-primary-600 hover:text-primary-800">
                  {{ alertInstance.entity_name }} ({{ alertInstance.entity_id }})
                </button>
              </dd>
            </div>

            <div class="space-y-1">
              <dt class="text-sm font-medium text-gray-500">Metric Value</dt>
              <dd class="text-sm text-gray-900 font-semibold">
                {{ formatValue(alertInstance.rate) }}
              </dd>
            </div>

            <div class="space-y-1">
              <dt class="text-sm font-medium text-gray-500">Attribution</dt>
              <dd class="text-sm text-gray-900">
                {{ alertInstance.attribution }} patients
              </dd>
            </div>

            <div class="space-y-1" v-if="alertInstance.percentile_rank">
              <dt class="text-sm font-medium text-gray-500">Percentile Rank</dt>
              <dd class="text-sm text-gray-900">
                {{ (alertInstance.percentile_rank * 100).toFixed(1) }}%
              </dd>
            </div>

            <div class="space-y-1">
              <dt class="text-sm font-medium text-gray-500">Assigned To</dt>
              <dd class="text-sm text-gray-900">
                <span v-if="alertInstance.assigned_to_first_name || alertInstance.assigned_to_last_name">
                  {{ alertInstance.assigned_to_first_name }} {{ alertInstance.assigned_to_last_name }}
                </span>
                <span v-else class="text-gray-400">Unassigned</span>
              </dd>
            </div>

            <div class="space-y-1">
              <dt class="text-sm font-medium text-gray-500">Last Status Change</dt>
              <dd class="text-sm text-gray-900">
                {{ alertInstance.last_status_change ? formatDate(alertInstance.last_status_change) : 'Never' }}
              </dd>
            </div>
          </dl>

          <!-- Status Note if exists -->
          <div v-if="alertInstance.status_note" class="mt-6 pt-6 border-t border-gray-100">
            <h3 class="text-sm font-medium text-gray-700 mb-2">Status Note</h3>
            <p class="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
              {{ alertInstance.status_note }}
            </p>
          </div>

          <!-- Action Buttons -->
          <div class="mt-6 pt-6 border-t border-gray-100 flex gap-3">
            <button
              v-if="alertInstance.status === 'active'"
              @click="acknowledgeAlert"
              class="btn-primary-sm"
            >
              <FontAwesomeIcon :icon="['fas', 'eye']" />
              Acknowledge
            </button>

            <button
              v-if="['active', 'acknowledged'].includes(alertInstance.status)"
              @click="resolveAlert"
              class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg font-medium transition-colors"
            >
              <FontAwesomeIcon :icon="['fas', 'check']" class="mr-2" />
              Resolve
            </button>

            <button
              @click="showAssignModal = true"
              class="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-lg font-medium transition-colors"
            >
              <FontAwesomeIcon :icon="['fas', 'user-plus']" class="mr-2" />
              Assign
            </button>

            <button
              @click="openNoteModal"
              class="btn-primary-sm"
            >
              <FontAwesomeIcon :icon="['fas', 'comment']" />
              Add Note
            </button>
          </div>
        </div>
      </div>

      <!-- Patient Details Section -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div class="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
          <h2 class="text-lg font-semibold text-gray-900 flex items-center">
            <FontAwesomeIcon :icon="['fas', 'users']" class="mr-2 text-primary-600" />
            Affected Patients
          </h2>
        </div>
        <div class="p-6">
          <AlertPatientTable
            :alert-instance-id="alertInstanceId"
          />
        </div>
      </div>
    </div>

    <!-- Add Note Modal -->
    <transition name="modal">
      <div v-if="showNoteModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
          <div class="fixed inset-0 transition-opacity" @click="showNoteModal = false">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>

          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Add Status Note</h3>
              <textarea
                v-model="newNote"
                rows="4"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark"
                placeholder="Enter note about this alert..."
              />
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                @click="saveNote"
                class="btn-primary w-full sm:ml-3 sm:w-auto sm:text-sm"
              >
                Save Note
              </button>
              <button
                @click="showNoteModal = false"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <!-- Add this modal after the Note modal -->
    <transition name="modal">
      <div v-if="showAssignModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
          <div class="fixed inset-0 transition-opacity" @click="showAssignModal = false">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>

          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Assign Alert</h3>
              <select
                v-model="selectedUserId"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark"
              >
                <option value="">Unassigned</option>
                <option v-for="user in availableUsers" :key="user.id" :value="user.id">
                  {{ user.last_name }}, {{ user.first_name }}
                </option>
              </select>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                @click="assignAlert"
                class="btn-primary w-full sm:ml-3 sm:w-auto sm:text-sm"
              >
                Assign
              </button>
              <button
                @click="showAssignModal = false"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import spiralDots from '@/assets/spiral_dots.svg'
import MetricAlertsTablePatientList from '@/components/Metric/Metric_Tables/Metric_Alerts_Table_Patient_List.vue'

export default {
  name: 'Overview_AlertInstance',

  components: {
    FontAwesomeIcon,
    AlertPatientTable: MetricAlertsTablePatientList
  },

  props: {
    alertInstanceId: {
      type: [Number, String],
      required: true
    }
  },

  emits: ['close'],

  data() {
    return {
      alertInstance: null,
      loading: false,
      backgroundImage: spiralDots,
      showNoteModal: false,
      showAssignModal: false,
      newNote: '',
      availableUsers:[],
      selectedUserId: null,
      changesMade: false
    }
  },



  async mounted() {
    await this.fetchAlertInstance()
    await this.fetchAvailableUsers()
  },

  methods: {
    async fetchAvailableUsers() {
      this.loadingOwners = true;
      try {
        // Fetch available team members from your API
        const response = await axios.get('api/user/');
        // sort available users based on last name, first_name
        this.availableUsers = response.data.sort((a, b) => {
          if (a.last_name === b.last_name) {
            return a.first_name.localeCompare(b.first_name);
          }
          return a.last_name.localeCompare(b.last_name);
        });
      } catch (error) {
        console.error('Error loading owners:', error);
        // Fallback to some default owners or empty array
        this.availableUsers = [];
      } finally {
        this.loadingOwners = false;
      }
    },

    openNoteModal() {
      this.newNote = this.alertInstance?.status_note || '';
      this.showNoteModal = true;
    },

    async assignAlert() {
      try {
        await axios.patch(`/api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`, {
          assigned_to_id: this.selectedUserId || null
        })
        this.showAssignModal = false
        this.selectedUserId = null
        await this.fetchAlertInstance()
        this.changesMade = true
      } catch (error) {
        console.error('Failed to assign alert:', error)
      }
    },
    async fetchAlertInstance() {
      this.loading = true
      try {
        // First get the alert instance from PostgreSQL
        const response = await axios.get(`/api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`)
        this.alertInstance = response.data
      } catch (error) {
        console.error('Failed to fetch alert instance:', error)
      } finally {
        this.loading = false
      }
    },

    async acknowledgeAlert() {
      try {
        await axios.patch(`/api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`, {
          status: 'acknowledged',
          last_status_change: new Date().toISOString()
        })
        await this.fetchAlertInstance()
        this.changesMade = true
      } catch (error) {
        console.error('Failed to acknowledge alert:', error)
      }
    },

    async resolveAlert() {
      try {
        await axios.patch(`/api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`, {
          status: 'resolved',
          last_status_change: new Date().toISOString()
        })
        await this.fetchAlertInstance()
        this.changesMade = true
      } catch (error) {
        console.error('Failed to resolve alert:', error)
      }
    },

    async saveNote() {
      try {
        await axios.patch(`/api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/`, {
          status_note: this.newNote
        })
        this.showNoteModal = false
        this.newNote = ''
        await this.fetchAlertInstance()
        this.changesMade = true
      } catch (error) {
        console.error('Failed to save note:', error)
      }
    },

    navigateToEntity() {
      if (this.alertInstance.entity_id) {
        this.$router.push(`/entity/${this.alertInstance.entity_id}`)
      }
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatValue(value) {
      if (value === null || value === undefined) return '—'
      return value.toFixed(3)
    },

    formatStatus(status) {
      const labels = {
        'active': 'Active',
        'acknowledged': 'Acknowledged',
        'resolved': 'Resolved',
        'snoozed': 'Snoozed',
        'erroneous': 'Erroneous',
        'expired': 'Aged Out'
      }
      return labels[status] || status
    },

    getStatusClass(status) {
      const classes = {
        'active': 'px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium',
        'acknowledged': 'px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium',
        'resolved': 'px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium',
        'snoozed': 'px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium',
        'erroneous': 'px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium',
        'expired': 'px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium'
      }
      return classes[status] || classes.active
    },

    handleClose() {
      // Emit close event with information about whether changes were made
      this.$emit('close', { refreshNeeded: this.changesMade });
    }
  },

  beforeUnmount() {
    // Emit close event when component is about to be destroyed
    this.handleClose();
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
</style>
